import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { getAllCompanies, createCompany as createCompanyApi } from "../../api/companies";
import axios, { AxiosError } from "axios";
import { AuthContext } from "../../context/auth-context/AuthContext";
import { useContext } from "react";
import { authReducerActionType } from "../../types/globalTypes";
import { showErrorMessage, showSuccessMessage } from "../../utils/toastUtils";
import { CreateCompanyDto } from "../../types/companyTypes";

const useCompanies = () => {
  const { dispatchAuth } = useContext(AuthContext);
  const queryClient = useQueryClient();

  // ############
  // GET QUERIES
  // ############

  // getAllCompanies query
  const { data: allCompaniesData } = useQuery({
    queryKey: ["companies"],
    queryFn: getAllCompanies,
  });
  const allCompanies = allCompaniesData ? allCompaniesData.data : [];

  // ----------------------------------------------------------------------------------
  // ############
  // CREATE QUERIES
  // ############

  // create new company mutation
  const { mutateAsync: createCompanyMutation } = useMutation({
    mutationFn: createCompanyApi,
    onError: (err: AxiosError) => {
      if (err.response?.status === 401) {
        dispatchAuth({ type: authReducerActionType.deauthenticate });
      }
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["companies"] });
      showSuccessMessage("Kunde erfolgreich erstellt");
    },
  });

  /**
   * Creates a new company
   * @param company The company data to create
   * @returns The created company as CompanyDetailsDto
   */
  const createCompany = async (company: CreateCompanyDto) => {
    try {
      return (await createCompanyMutation(company)).data;
    } catch (error: unknown) {
      if (axios.isAxiosError(error) && error.response?.status === 409) {
        const message = error.response.data?.message;

        // TODO: Not ideal, use custom problem codes from the backend to differentiate the error
        if (message === `Company with name '${company.name}' already exists`) {
          showErrorMessage("Es existiert bereits ein Kunde mit diesem Namen");
          return;
        }

        // TODO: Not ideal, use custom problem codes from the backend to differentiate the error
        if (message === `Company with URL '${company.url}' already exists`) {
          showErrorMessage("Es existiert bereits ein Kunde mit dieser Website");
          return;
        }
      } else {
        showErrorMessage("Kunde erstellen fehlgeschlagen");
        return;
      }
    }
  };

  return { allCompanies, createCompany };
};

export default useCompanies;
